"use client";

import React from "react";
import { type AttendeeData } from "../../../lib/attendee-utils.js";

interface SummaryStatsProps {
  attendees: AttendeeData[];
  showActions?: boolean;
  onCopyAll?: () => void;
  onEmailAll?: () => void;
  className?: string;
}

export function SummaryStats({
  attendees,
  showActions = true,
  onCopyAll,
  onEmailAll,
  className = ""
}: SummaryStatsProps) {
  const stats = React.useMemo(() => {
    const accepted = attendees.filter(a => a.responseStatus?.toLowerCase() === "accepted").length;
    const declined = attendees.filter(a => a.responseStatus?.toLowerCase() === "declined").length;
    const tentative = attendees.filter(a => a.responseStatus?.toLowerCase() === "tentative").length;
    const noResponse = attendees.filter(a => 
      !a.responseStatus || 
      a.responseStatus.toLowerCase() === "needsaction" || 
      a.responseStatus.toLowerCase() === "needs_action"
    ).length;

    return { accepted, declined, tentative, noResponse, total: attendees.length };
  }, [attendees]);

  return (
    <div className={`flex items-center justify-between p-4 bg-card border border-gray-200 rounded-lg ${className}`}>
      <div className="flex items-center space-x-6">
        <div className="text-center">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-xs text-muted-foreground">Total</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-700">{stats.accepted}</div>
          <div className="text-xs text-muted-foreground">Accepted</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-700">{stats.declined}</div>
          <div className="text-xs text-muted-foreground">Declined</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-700">{stats.tentative}</div>
          <div className="text-xs text-muted-foreground">Tentative</div>
        </div>
        {stats.noResponse > 0 && (
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-700">{stats.noResponse}</div>
            <div className="text-xs text-muted-foreground">No Response</div>
          </div>
        )}
      </div>
      
      {showActions && (onCopyAll || onEmailAll) && (
        <div className="flex items-center space-x-2">
          {onCopyAll && (
            <button type="button"
              onClick={onCopyAll}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-card-50 transition-colors"
            >
              <span>Copy All</span>
            </button>
          )}
          {onEmailAll && (
            <button type="button"
              onClick={onEmailAll}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-card-50 transition-colors"
            >
              <span>Email All</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
